import React, { useState, useEffect, useCallback, useMemo } from "react";
import { C, TRANSACTION_TYPES, TRANSACTION_STATUSES, PAYMENT_METHODS, STRATEGIC_PLAN, COLOR_TEMPLATES, REQUEST_CATEGORIES, REQUEST_STATUSES, APPROVAL_ROUTES, getPerms } from "./constants";
import { getRequests, getRequestTrail, advanceRequest, getMeetings, createMeeting, getTransactions, createTransaction, getCurrentUser } from "./supabaseClient";
import {
  validateAttachmentFile, buildAttachmentPath, uploadFile, getSignedUrl, deleteFile,
  createAttachment, getAttachments, deleteAttachment,
  ATTACHMENTS_BUCKET, MAX_ATTACHMENT_SIZE, ALLOWED_ATTACHMENT_EXTS,
} from "./supabaseClient";
import {
  X, Plus, FileText, Calendar, DollarSign, TrendingUp,
  CheckCircle, Clock, AlertCircle, XCircle,
  Download, Users, Target, Paperclip,
  ArrowLeft, Save,
  CreditCard, Receipt, Check,
  Inbox, ThumbsUp, ThumbsDown, RefreshCw,
  Upload, Trash2, Loader
} from "lucide-react";

// ===== useToast helper =====
export function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);
  const ToastView = toast ? (
    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
  ) : null;
  return [showToast, ToastView];
}

// ===== Card =====
export function Card({ children, style, className = "", onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={className}
      style={{
        background: C.white,
        borderRadius: 16,
        padding: 24,
        border: `1px solid ${C.border}`,
        boxShadow: hover
          ? "0 4px 16px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ===== Btn =====
export function Btn({ children, onClick, variant = "primary", size = "md", icon: Icon, disabled, style, type = "button" }) {
  const variants = {
    primary: { bg: C.accent, color: C.primary, hoverBg: "#c9ad4e", border: "none" },
    danger:  { bg: C.danger,  color: "#fff",    hoverBg: "#a83e2e", border: "none" },
    ghost:   { bg: "transparent", color: C.primary, hoverBg: "#f4f5f7", border: `1px solid ${C.border}` },
    success: { bg: C.success, color: "#fff",    hoverBg: "#236e47", border: "none" },
    teal:    { bg: C.teal,    color: "#fff",    hoverBg: "#009688", border: "none" },
    outline: { bg: "transparent", color: C.primary, hoverBg: "#f4f5f7", border: `1px solid ${C.border}` },
  };
  const v = variants[variant] || variants.primary;
  const sizes = {
    sm: { padding: "7px 14px",  fontSize: 12 },
    md: { padding: "10px 20px", fontSize: 14 },
    lg: { padding: "13px 26px", fontSize: 15 },
  };
  const s = sizes[size] || sizes.md;

  const [hover, setHover] = useState(false);

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: disabled ? "#e8e8e8" : hover ? v.hoverBg : v.bg,
        color: disabled ? "#999" : v.color,
        border: v.border,
        borderRadius: 10,
        fontFamily: "Readex Pro, sans-serif",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
        ...s,
        ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 14 : size === "lg" ? 18 : 16} />}
      {children}
    </button>
  );
}

// ===== Field =====
export function Field({ label, type = "text", value, onChange, options, placeholder, required, rows = 3, disabled, style, min, max, step }) {
  const base = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    fontFamily: "Readex Pro, sans-serif",
    fontSize: 14,
    color: C.text,
    background: disabled ? "#f5f5f5" : C.white,
    direction: "rtl",
    transition: "border-color 0.15s, box-shadow 0.15s",
    outline: "none",
  };

  return (
    <div style={{ marginBottom: 16, ...style }}>
      {label && (
        <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textLight }}>
          {label} {required && <span style={{ color: C.danger }}>*</span>}
        </label>
      )}
      {type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} style={{ ...base, cursor: "pointer" }}>
          <option value="">{placeholder || "اختر..."}</option>
          {(options || []).map((o) => (
            <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
              {typeof o === "string" ? o : o.label}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled} style={{ ...base, resize: "vertical" }} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} min={min} max={max} step={step} style={base} />
      )}
    </div>
  );
}

// ===== Toast =====
export function Toast({ message, type = "success", onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = { success: C.success, error: C.danger, warning: C.warning, info: C.teal };
  const icons = { success: CheckCircle, error: XCircle, warning: AlertCircle, info: AlertCircle };
  const Icon = icons[type];

  return (
    <div
      style={{
        position: "fixed",
        top: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: colors[type],
        color: "#fff",
        padding: "14px 28px",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
        zIndex: 9999,
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        animation: "fadeIn 0.3s ease-out",
        fontWeight: 600,
        fontSize: 14,
      }}
    >
      <Icon size={18} />
      {message}
      <X size={16} style={{ cursor: "pointer", opacity: 0.7, marginRight: 8 }} onClick={onClose} />
    </div>
  );
}

// ===== ModuleHeader =====
export function ModuleHeader({ icon: Icon, title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 4px 12px ${C.primary}25`,
      }}>
        <Icon size={28} color="#fff" />
      </div>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: C.primary, margin: 0, lineHeight: 1.3 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: C.textLight, margin: "4px 0 0", lineHeight: 1.6 }}>{subtitle}</p>}
      </div>
      {children && (
        <div style={{ marginInlineStart: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ===== KPICard =====
export function KPICard({ title, value, icon: Icon, color = C.teal, subtitle, trend }) {
  const strVal = typeof value === "number" ? value.toLocaleString("ar-SA") : String(value);
  const len = strVal.length;
  const fontSize = len > 8 ? 18 : len > 6 ? 22 : len > 4 ? 26 : 32;
  return (
    <Card style={{ minHeight: 120, padding: 18, position: "relative", overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 12, color: C.textLight, margin: 0, marginBottom: 8, fontWeight: 500, lineHeight: 1.4 }}>{title}</p>
          <p style={{ fontSize, fontWeight: 700, color: C.primary, margin: 0, lineHeight: 1.2, whiteSpace: "nowrap" }}>
            {strVal}
          </p>
          {subtitle && <p style={{ fontSize: 12, color: C.textLight, margin: "6px 0 0" }}>{subtitle}</p>}
          {trend && (
            <p style={{
              display: "inline-flex", alignItems: "center", gap: 2,
              fontSize: 12, color: String(trend).startsWith("-") ? C.danger : C.success,
              margin: "8px 0 0", fontWeight: 700,
              padding: "2px 8px", borderRadius: 6,
              background: String(trend).startsWith("-") ? `${C.danger}10` : `${C.success}10`,
            }}>
              {String(trend).startsWith("+") || String(trend).startsWith("-") ? trend : `+${trend}`}
              {typeof trend === "number" || (typeof trend === "string" && /^[+-]?\d/.test(trend)) ? "%" : ""}
            </p>
          )}
        </div>
        <div style={{
          width: 48, height: 48, borderRadius: "50%",
          background: `${color}15`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Icon size={22} color={color} />
        </div>
      </div>
    </Card>
  );
}

// ===== TabBar =====
export function TabBar({ tabs, active, onChange }) {
  // Tighten font/padding when many tabs to allow wrapping
  const many = tabs.length > 8;
  return (
    <div style={{
      display: "flex",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      gap: 6,
      marginBottom: 20,
      paddingBottom: 0,
      borderBottom: `1px solid ${C.border}`,
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    }}>
      <style>{`.erp-tabbar::-webkit-scrollbar { display: none; }`}</style>
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              padding: many ? "9px 14px" : "12px 18px",
              border: "none",
              borderBottom: isActive ? `3px solid ${C.teal}` : "3px solid transparent",
              background: isActive ? `${C.teal}08` : "transparent",
              color: isActive ? C.teal : C.textLight,
              fontFamily: "Readex Pro, sans-serif",
              fontWeight: isActive ? 700 : 500,
              fontSize: many ? 12 : 13,
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all 0.15s",
              borderRadius: "10px 10px 0 0",
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ===== Modal =====
export function Modal({ title, onClose, children, width = 600 }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: isMobile ? "flex-end" : "center",
        justifyContent: "center",
        zIndex: 1000,
        animation: "fadeIn 0.2s",
        padding: isMobile ? 0 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          borderRadius: isMobile ? "20px 20px 0 0" : 20,
          width: "100%",
          maxWidth: isMobile ? "100%" : width,
          maxHeight: isMobile ? "92vh" : "88vh",
          overflow: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          animation: "scaleIn 0.2s ease-out",
        }}
      >
        <div style={{
          padding: "20px 32px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          background: C.white,
          zIndex: 1,
          borderRadius: "20px 20px 0 0",
        }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: C.primary, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex", alignItems: "center" }}>
            <X size={22} color={C.textLight} />
          </button>
        </div>
        <div style={{ padding: 32 }}>{children}</div>
      </div>
    </div>
  );
}

// ===== StatusBadge =====
export function StatusBadge({ status, statuses = TRANSACTION_STATUSES }) {
  const s = statuses.find((st) => st.key === status) || { label: status, color: C.textLight };
  return (
    <span style={{
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      background: `${s.color}18`,
      color: s.color,
      whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

// ===== EmptyState =====
export function EmptyState({ icon: Icon = FileText, title = "لا توجد بيانات", subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: 80, color: C.textLight }}>
      <Icon size={56} strokeWidth={1} style={{ marginBottom: 20, opacity: 0.35 }} />
      <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: C.primary }}>{title}</p>
      {subtitle && <p style={{ fontSize: 14, lineHeight: 1.7 }}>{subtitle}</p>}
    </div>
  );
}

// ===== DataTable =====
export function DataTable({ columns, data, onRowClick }) {
  if (!data || data.length === 0) return <EmptyState />;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.border}`, background: C.white }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th key={col.key} style={{
                padding: "14px 16px",
                fontSize: 13,
                fontWeight: 700,
                color: C.textLight,
                borderBottom: `1px solid ${C.border}`,
                background: "#fafafa",
                whiteSpace: "nowrap",
                textAlign: "right",
                borderTopRightRadius: idx === 0 ? 12 : 0,
                borderTopLeftRadius: idx === columns.length - 1 ? 12 : 0,
              }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={() => onRowClick && onRowClick(row)}
              style={{ cursor: onRowClick ? "pointer" : "default", transition: "background 0.12s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbfc")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {columns.map((col) => (
                <td key={col.key} style={{
                  padding: "14px 16px",
                  fontSize: 14,
                  color: C.text,
                  borderBottom: i === data.length - 1 ? "none" : `1px solid ${C.borderLight}`,
                  whiteSpace: col.nowrap ? "nowrap" : "normal",
                  lineHeight: 1.6,
                }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===== MeetingsTab =====
const SHARED_MEETING_TYPES = ["دوري", "غير دوري", "طارئ"];
const SHARED_MEETING_TYPE_COLORS = { "دوري": "#7c3aed", "غير دوري": C.teal, "طارئ": C.danger };
const SHARED_INITIAL_MEETING = {
  title: "", date: "", time: "", type: "دوري",
  link: "", attendees: "", absentees: "", agenda: "",
  notes: "",
};

export function MeetingsTab({ departmentName = "الإدارة", module = "general", user, canEdit = true }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(SHARED_INITIAL_MEETING);
  const [attachRefresh, setAttachRefresh] = useState(0);
  const [showToast, ToastView] = useToast();

  // فحص الدور الإداري عبر الصلاحيات المدموجة (getPerms — يدعم roles[]).
  // حذف المرفقات متاح فقط للأدوار الإدارية؛ آمن عند غياب user.
  // fallback: عند عدم تمرير prop user نقرأ المستخدم الحالي من التخزين المحلي.
  const effectiveUser = user || getCurrentUser();
  const canManageAttachments = !!(effectiveUser && getPerms(effectiveUser).adminOnly);

  const splitList = s => s ? s.split(/،|,|\n/).map(x => x.trim()).filter(Boolean) : [];

  // تحويل صف Supabase إلى شكل الواجهة.
  // الأعمدة المخزّنة: module/title/meeting_date/meeting_time/meeting_type/link/location/attendees/absentees/agenda/notes.
  // المحضر (minutes) سيُغطَّى لاحقًا بنظام المرفقات الحقيقي، وليس كعمود نصّي.
  const mapRow = (row) => ({
    id: row.id,                       // uuid الحقيقي القادم من Supabase
    title: row.title || "",
    date: row.meeting_date || "",
    time: row.meeting_time || "",
    type: row.meeting_type || "",
    link: row.link || "",
    attendees: splitList(row.attendees),
    absentees: splitList(row.absentees),
    agenda: row.agenda || "",
    notes: row.notes || "",
    status: row.meeting_date && row.meeting_date < new Date().toISOString().slice(0, 10)
      ? "completed" : "scheduled",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError("");
    getMeetings(module)
      .then((rows) => { if (alive) setMeetings((rows || []).map(mapRow)); })
      .catch((e) => { if (alive) setLoadError(e?.message || "تعذّر جلب الاجتماعات"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  async function handleSave() {
    if (!form.title || !form.date) {
      showToast("العنوان والتاريخ مطلوبان", "error");
      return;
    }
    try {
      const saved = await createMeeting({
        module,
        title: form.title,
        meeting_date: form.date,
        meeting_time: form.time || "",
        meeting_type: form.type || "",
        link: form.link || "",
        location: "",
        attendees: splitList(form.attendees).join("، "),
        absentees: splitList(form.absentees).join("، "),
        agenda: form.agenda || "",
        notes: form.notes || "",
      });
      if (saved) setMeetings(prev => [mapRow(saved), ...prev]);
      setForm(SHARED_INITIAL_MEETING);
      setShowForm(false);
      showToast("تم حفظ الاجتماع");
    } catch (e) {
      showToast(e?.message || "تعذّر حفظ الاجتماع", "error");
    }
  }

  const statusLabels = { completed: "منعقد", scheduled: "مجدول", cancelled: "ملغي" };
  const statusColors = { completed: C.success, scheduled: C.teal, cancelled: C.danger };

  return (
    <div>
      {ToastView}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.primary, margin: 0 }}>اجتماعات {departmentName}</h3>
        {canEdit && <Btn icon={Plus} onClick={() => setShowForm(true)}>اجتماع جديد</Btn>}
      </div>

      {loadError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, marginBottom: 14, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 10, color: C.danger, fontSize: 13 }}>
          <AlertCircle size={16} /> {loadError}
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, color: C.textLight, fontSize: 14 }}>
          <RefreshCw size={16} /> جارٍ تحميل الاجتماعات...
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {meetings.map((m) => (
          <Card key={m.id} onClick={() => setSelected(m)} style={{ cursor: "pointer", borderRight: `4px solid ${SHARED_MEETING_TYPE_COLORS[m.type] || statusColors[m.status]}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <h4 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: 0 }}>{m.title}</h4>
                  {m.type && (
                    <span style={{
                      padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                      background: `${SHARED_MEETING_TYPE_COLORS[m.type] || C.teal}15`,
                      color: SHARED_MEETING_TYPE_COLORS[m.type] || C.teal,
                    }}>{m.type}</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 20, fontSize: 13, color: C.textLight, flexWrap: "wrap" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={13} /> {m.date}</span>
                  {m.time && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Clock size={13} /> {m.time}</span>}
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Users size={13} /> {(m.attendees || []).length} حضور</span>
                </div>
              </div>
              <span style={{
                padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                background: `${statusColors[m.status]}15`, color: statusColors[m.status],
              }}>
                {statusLabels[m.status]}
              </span>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={650}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 16, fontSize: 13, color: C.textLight, flexWrap: "wrap" }}>
              <span><Calendar size={13} /> {selected.date}</span>
              {selected.time && <span><Clock size={13} /> {selected.time}</span>}
              {selected.type && <span style={{ padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${SHARED_MEETING_TYPE_COLORS[selected.type]}15`, color: SHARED_MEETING_TYPE_COLORS[selected.type] }}>{selected.type}</span>}
            </div>

            {selected.link && (
              <div style={{ padding: 10, background: `${C.teal}08`, borderRadius: 8, fontSize: 13 }}>
                <strong style={{ color: C.teal }}>رابط الاجتماع: </strong>
                <a href={selected.link} target="_blank" rel="noopener noreferrer" dir="ltr" style={{ color: C.primary, wordBreak: "break-all" }}>{selected.link}</a>
              </div>
            )}

            {(selected.attendees || []).length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.success }}>الحضور ({selected.attendees.length}):</p>
                <p style={{ margin: "4px 0 0", fontSize: 14, color: C.primary }}>{selected.attendees.join("، ")}</p>
              </div>
            )}

            {(selected.absentees || []).length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.danger }}>الغياب ({selected.absentees.length}):</p>
                <p style={{ margin: "4px 0 0", fontSize: 14, color: C.primary }}>{selected.absentees.join("، ")}</p>
              </div>
            )}

            {selected.agenda && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>جدول الأعمال:</p>
                <p style={{ margin: "4px 0 0", padding: 12, background: C.bg, borderRadius: 8, fontSize: 14, color: C.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.agenda}</p>
              </div>
            )}

            {selected.notes && (
              <p style={{ margin: 0, padding: 10, background: C.bg, borderRadius: 8, fontSize: 13, color: C.textLight }}>
                <strong>ملاحظات:</strong> {selected.notes}
              </p>
            )}

            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.primary, display: "flex", alignItems: "center", gap: 6 }}>
                <Paperclip size={16} color={C.teal} /> محضر الاجتماع والمرفقات
              </p>
              {selected.id ? (
                <>
                  {canEdit && <FileUpload module="meeting" recordId={selected.id} onUploaded={() => setAttachRefresh((k) => k + 1)} />}
                  <AttachmentList module="meeting" recordId={selected.id} canDelete={canEdit && canManageAttachments} refreshKey={attachRefresh} />
                </>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertCircle size={14} /> احفظ الاجتماع أولًا لإرفاق المحضر
                </p>
              )}
            </div>

            <Btn variant="ghost" icon={ArrowLeft} onClick={() => setSelected(null)}>إغلاق</Btn>
          </div>
        </Modal>
      )}

      {canEdit && showForm && (
        <Modal title="جدولة اجتماع جديد" onClose={() => { setShowForm(false); setForm(SHARED_INITIAL_MEETING); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان الاجتماع" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
              <Field label="الوقت" type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
              <Field label="نوع الاجتماع" type="select" options={SHARED_MEETING_TYPES} value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
            </div>
            <Field label="رابط الاجتماع الإلكتروني" placeholder="https://zoom.us/..." value={form.link} onChange={(v) => setForm({ ...form, link: v })} />
            <Field label="الحضور (مفصولة بفواصل)" type="textarea" rows={2} placeholder="اسم1، اسم2، اسم3..." value={form.attendees} onChange={(v) => setForm({ ...form, attendees: v })} />
            <Field label="الغياب (مفصولة بفواصل)" type="textarea" rows={2} placeholder="اسم1، اسم2..." value={form.absentees} onChange={(v) => setForm({ ...form, absentees: v })} />
            <Field label="جدول الأعمال" type="textarea" rows={4} placeholder="1. ...&#10;2. ...&#10;3. ..." value={form.agenda} onChange={(v) => setForm({ ...form, agenda: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(SHARED_INITIAL_MEETING); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== TransactionsTab =====
const TX_INITIAL_FORM = { date: "", type: "", description: "", beneficiary: "", amount: "", paymentMethod: "", invoiceNo: "", status: "pending", attachment: "", notes: "" };

export function TransactionsTab({ budget = 0, module = "" }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState({ type: "", status: "", search: "" });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(TX_INITIAL_FORM);
  const [showToast, ToastView] = useToast();

  // تحويل صف Supabase (snake_case) إلى شكل الواجهة.
  // الأعمدة المخزّنة: module/txn_date/txn_type/description/beneficiary/amount/
  // payment_method/invoice_number/status/attachment/notes.
  const mapRow = (row) => ({
    id: row.id,
    date: row.txn_date || "",
    type: row.txn_type || "",
    description: row.description || "",
    beneficiary: row.beneficiary || "",
    amount: Number(row.amount) || 0,
    paymentMethod: row.payment_method || "",
    invoiceNo: row.invoice_number || "",
    status: row.status || "pending",
    attachment: row.attachment || "",
    notes: row.notes || "",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError("");
    getTransactions(module)
      .then((rows) => { if (alive) setTransactions((rows || []).map(mapRow)); })
      .catch((e) => { if (alive) setLoadError(e?.message || "تعذّر جلب المعاملات"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  async function handleSave() {
    if (!form.date || !form.type || !form.amount) {
      showToast("التاريخ والنوع والمبلغ مطلوبة", "error");
      return;
    }
    try {
      const saved = await createTransaction({
        module,
        txn_date: form.date,
        txn_type: form.type,
        description: form.description || "",
        beneficiary: form.beneficiary || "",
        amount: Number(form.amount) || 0,
        payment_method: form.paymentMethod || "",
        invoice_number: form.invoiceNo || "",
        status: form.status || "pending",
        attachment: form.attachment || "",
        notes: form.notes || "",
      });
      if (saved) setTransactions((prev) => [mapRow(saved), ...prev]);
      setForm(TX_INITIAL_FORM);
      setShowForm(false);
      showToast("تم حفظ المعاملة");
    } catch (e) {
      showToast(e?.message || "تعذّر حفظ المعاملة", "error");
    }
  }

  const totalExpenses = transactions.filter((t) => t.status === "paid").reduce((s, t) => s + t.amount, 0);
  const remaining = budget - totalExpenses;

  const filtered = transactions.filter((t) => {
    if (filter.type && t.type !== filter.type) return false;
    if (filter.status && t.status !== filter.status) return false;
    if (filter.search && !t.description.includes(filter.search) && !t.beneficiary.includes(filter.search)) return false;
    return true;
  });

  return (
    <div>
      {ToastView}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20, marginBottom: 20 }}>
        <KPICard title="الميزانية المخصصة" value={budget.toLocaleString("ar-SA")} icon={DollarSign} color={C.primary} subtitle="ريال" />
        <KPICard title="إجمالي المصروفات" value={totalExpenses.toLocaleString("ar-SA")} icon={TrendingUp} color={C.danger} subtitle="ريال" />
        <KPICard title="المتبقي" value={remaining.toLocaleString("ar-SA")} icon={CreditCard} color={C.success} subtitle="ريال" />
        <KPICard title="عدد المعاملات" value={transactions.length} icon={Receipt} color={C.teal} />
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Field label="بحث" value={filter.search} onChange={(v) => setFilter({ ...filter, search: v })} placeholder="بحث بالوصف أو المستفيد..." style={{ marginBottom: 0 }} />
          </div>
          <div style={{ minWidth: 150 }}>
            <Field label="النوع" type="select" value={filter.type} onChange={(v) => setFilter({ ...filter, type: v })} options={TRANSACTION_TYPES} style={{ marginBottom: 0 }} />
          </div>
          <div style={{ minWidth: 150 }}>
            <Field label="الحالة" type="select" value={filter.status} onChange={(v) => setFilter({ ...filter, status: v })} options={TRANSACTION_STATUSES.map((s) => ({ value: s.key, label: s.label }))} style={{ marginBottom: 0 }} />
          </div>
          <Btn icon={Plus} onClick={() => setShowForm(true)}>إضافة معاملة</Btn>
        </div>
      </Card>

      {loadError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, marginBottom: 14, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 10, color: C.danger, fontSize: 13 }}>
          <AlertCircle size={16} /> {loadError}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, color: C.textLight, fontSize: 14 }}>
          <RefreshCw size={16} /> جارٍ تحميل المعاملات...
        </div>
      ) : transactions.length === 0 && !loadError ? (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 40, color: C.textLight }}>
            <Receipt size={32} color="#ccc" />
            <div style={{ fontSize: 14 }}>لا توجد معاملات</div>
          </div>
        </Card>
      ) : (
        /* Table */
        <Card>
          <DataTable
            columns={[
              { key: "id", label: "#", render: (v) => <span style={{ fontWeight: 600, color: C.textLight }}>#{typeof v === "string" ? v.slice(0, 8) : v}</span> },
              { key: "date", label: "التاريخ", nowrap: true },
              { key: "type", label: "النوع", render: (v) => <span style={{ padding: "2px 8px", background: `${C.primary}08`, borderRadius: 6, fontSize: 11, fontWeight: 500 }}>{v}</span> },
              { key: "description", label: "الوصف" },
              { key: "beneficiary", label: "المستفيد" },
              { key: "amount", label: "المبلغ", nowrap: true, render: (v) => <span style={{ fontWeight: 700, color: C.primary }}>{(Number(v) || 0).toLocaleString("ar-SA")} ر.س</span> },
              { key: "paymentMethod", label: "طريقة الدفع" },
              { key: "invoiceNo", label: "رقم الفاتورة", render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{v}</span> },
              { key: "status", label: "الحالة", render: (v) => <StatusBadge status={v} /> },
              { key: "attachment", label: "مرفق", render: (v) => v ? <Paperclip size={14} color={C.teal} /> : <span style={{ color: "#ccc" }}>—</span> },
            ]}
            data={filtered}
          />
        </Card>
      )}

      {/* Add Form */}
      {showForm && (
        <Modal title="إضافة معاملة جديدة" onClose={() => setShowForm(false)} width={700}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="التاريخ" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
            <Field label="النوع" type="select" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TRANSACTION_TYPES} required />
            <Field label="المبلغ" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} required />
            <Field label="طريقة الدفع" type="select" value={form.paymentMethod} onChange={(v) => setForm({ ...form, paymentMethod: v })} options={PAYMENT_METHODS} />
            <Field label="المستفيد" value={form.beneficiary} onChange={(v) => setForm({ ...form, beneficiary: v })} required />
            <Field label="رقم الفاتورة" value={form.invoiceNo} onChange={(v) => setForm({ ...form, invoiceNo: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={TRANSACTION_STATUSES.map((s) => ({ value: s.key, label: s.label }))} />
            <Field label="اسم المرفق" value={form.attachment} onChange={(v) => setForm({ ...form, attachment: v })} placeholder="اسم الملف" />
          </div>
          <Field label="الوصف" value={form.description} onChange={(v) => setForm({ ...form, description: v })} required />
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} rows={2} />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Btn icon={Save} onClick={handleSave}>حفظ</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>إلغاء</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ===== OperationalPlanTab — Rich Goals + Tasks =====
const TASK_STATUS_META = {
  not_started: { label: "لم يبدأ", color: C.textLight },
  in_progress: { label: "جاري", color: C.warning },
  completed: { label: "مكتمل", color: C.success },
  delayed: { label: "متأخر", color: C.danger },
};
const TASK_STATUS_OPTIONS = [
  { value: "not_started", label: "لم يبدأ" },
  { value: "in_progress", label: "جاري" },
  { value: "completed", label: "مكتمل" },
  { value: "delayed", label: "متأخر" },
];
const RATING_OPTIONS = ["", "ممتاز", "جيد", "مقبول", "ضعيف"];
const PRIORITY_OPTIONS = [
  { value: "high", label: "عالية", color: C.danger },
  { value: "medium", label: "متوسطة", color: C.warning },
  { value: "low", label: "منخفضة", color: C.teal },
];

function opGoalProgress(goal) {
  if (!goal.tasks || goal.tasks.length === 0) return 0;
  const done = goal.tasks.filter(t => t.status === "completed").length;
  return Math.round((done / goal.tasks.length) * 100);
}

const INITIAL_OP_GOAL = {
  name: "", description: "", owner: "", priority: "medium",
  startDate: "", endDate: "", weight: "", indicators: "", budget: "",
};
const INITIAL_OP_TASK = {
  title: "", description: "", owner: "",
  startDate: "", deadline: "",
  status: "not_started", rating: "", notes: "",
};

export function OperationalPlanTab({ departmentKey, canEdit = true }) {
  const initial = STRATEGIC_PLAN[departmentKey];
  const [goals, setGoals] = useState(initial?.goals || []);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showTaskFormFor, setShowTaskFormFor] = useState(null);
  const [goalForm, setGoalForm] = useState(INITIAL_OP_GOAL);
  const [taskForm, setTaskForm] = useState(INITIAL_OP_TASK);
  const [showToast, ToastView] = useToast();

  if (!initial) return <EmptyState title="لا توجد خطة تشغيلية لهذه الإدارة" />;

  // KPIs
  const totalGoals = goals.length;
  const achievedGoals = goals.filter(g => opGoalProgress(g) === 100).length;
  const inProgressGoals = goals.filter(g => {
    const p = opGoalProgress(g);
    return p > 0 && p < 100;
  }).length;
  const totalWeight = goals.reduce((s, g) => s + (Number(g.weight) || 0), 0) || 1;
  const planProgress = Math.round(
    goals.reduce((s, g) => s + opGoalProgress(g) * (Number(g.weight) || 0), 0) / totalWeight
  );

  function toggleTaskComplete(goalId, taskId) {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        tasks: g.tasks.map(t => {
          if (t.id !== taskId) return t;
          return { ...t, status: t.status === "completed" ? "in_progress" : "completed" };
        }),
      };
    }));
  }

  function handleSaveGoal() {
    if (!goalForm.name) {
      showToast("اسم الهدف مطلوب", "error");
      return;
    }
    setGoals(prev => [...prev, {
      id: `g${Date.now()}`,
      name: goalForm.name,
      description: goalForm.description,
      owner: goalForm.owner,
      priority: goalForm.priority,
      startDate: goalForm.startDate,
      endDate: goalForm.endDate,
      weight: Number(goalForm.weight) || 0,
      indicators: goalForm.indicators,
      budget: Number(goalForm.budget) || 0,
      tasks: [],
    }]);
    setGoalForm(INITIAL_OP_GOAL);
    setShowGoalForm(false);
    showToast("تمت إضافة الهدف");
  }

  function handleSaveTask() {
    if (!taskForm.title) {
      showToast("عنوان المهمة مطلوب", "error");
      return;
    }
    setGoals(prev => prev.map(g => {
      if (g.id !== showTaskFormFor) return g;
      return {
        ...g,
        tasks: [...g.tasks, {
          id: `t${Date.now()}`,
          title: taskForm.title,
          description: taskForm.description,
          owner: taskForm.owner,
          startDate: taskForm.startDate,
          deadline: taskForm.deadline,
          status: taskForm.status,
          rating: taskForm.rating,
          notes: taskForm.notes,
        }],
      };
    }));
    setTaskForm(INITIAL_OP_TASK);
    setShowTaskFormFor(null);
    showToast("تمت إضافة المهمة");
  }

  return (
    <div>
      {ToastView}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: 0 }}>
          الخطة التشغيلية — {initial.name}
        </h3>
        {canEdit && <Btn icon={Plus} onClick={() => setShowGoalForm(true)} size="sm">إضافة هدف جديد</Btn>}
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        <KPICard title="عدد الأهداف" value={totalGoals} icon={Target} color={C.teal} />
        <KPICard title="الأهداف المحققة" value={achievedGoals} icon={CheckCircle} color={C.success} />
        <KPICard title="الأهداف الجارية" value={inProgressGoals} icon={Clock} color={C.warning} />
        <KPICard title="نسبة التحقق الإجمالية" value={`${planProgress}%`} icon={TrendingUp} color={C.primary} />
      </div>

      {/* Goals */}
      <div style={{ display: "grid", gap: 14 }}>
        {goals.map(g => {
          const prog = opGoalProgress(g);
          const isAchieved = prog === 100;
          const goalColor = isAchieved ? C.success : prog >= 50 ? C.teal : C.warning;
          const completedTasks = g.tasks.filter(t => t.status === "completed").length;
          const priorityMeta = PRIORITY_OPTIONS.find(p => p.value === g.priority) || PRIORITY_OPTIONS[1];

          return (
            <Card key={g.id} style={{ padding: 18, borderRight: `4px solid ${goalColor}` }}>
              {/* Goal header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 250 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{g.name}</h4>
                    {isAchieved && (
                      <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${C.success}15`, color: C.success, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Check size={10} /> متحقق
                      </span>
                    )}
                    <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${priorityMeta.color}15`, color: priorityMeta.color }}>
                      {priorityMeta.label}
                    </span>
                  </div>
                  {g.description && (
                    <p style={{ margin: 0, fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>{g.description}</p>
                  )}
                </div>
                <div style={{ textAlign: "left", minWidth: 130 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: goalColor }}>{prog}%</div>
                  <div style={{ fontSize: 11, color: C.textLight }}>{completedTasks}/{g.tasks.length} مهام</div>
                </div>
              </div>

              {/* Meta */}
              {(g.owner || g.startDate || g.endDate || g.budget > 0) && (
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: C.textLight, marginBottom: 8 }}>
                  {g.owner && <span><strong style={{ color: C.primary }}>المسؤول:</strong> {g.owner}</span>}
                  {g.startDate && <span><strong style={{ color: C.primary }}>البداية:</strong> {g.startDate}</span>}
                  {g.endDate && <span><strong style={{ color: C.primary }}>النهاية:</strong> {g.endDate}</span>}
                  {g.budget > 0 && <span><strong style={{ color: C.primary }}>الميزانية:</strong> {g.budget.toLocaleString()} ر.س</span>}
                </div>
              )}

              {g.indicators && (
                <div style={{ padding: 10, background: `${C.teal}06`, borderRadius: 8, marginBottom: 10 }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.teal }}>مؤشرات الأداء:</p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{g.indicators}</p>
                </div>
              )}

              {/* Progress bar */}
              <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
                <div style={{ height: "100%", width: `${prog}%`, background: goalColor, transition: "width 0.4s" }} />
              </div>

              {/* Tasks table */}
              {g.tasks.length === 0 ? (
                <p style={{ fontSize: 12, color: C.textLight, margin: 0 }}>لا توجد مهام بعد.</p>
              ) : (
                <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 6 }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "center", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 36 }}><Check size={14} style={{ verticalAlign: "middle" }} /></th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}` }}>المهمة</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 120 }}>المسؤول</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 110 }}>البداية</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 110 }}>الموعد</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 90 }}>الحالة</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 80 }}>التقييم</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.tasks.map(t => {
                        const meta = TASK_STATUS_META[t.status] || TASK_STATUS_META.not_started;
                        return (
                          <tr key={t.id}>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}`, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={t.status === "completed"}
                                disabled={!canEdit}
                                onChange={() => toggleTaskComplete(g.id, t.id)}
                                title="تأشير كمكتمل"
                              />
                            </td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>
                              <span style={{
                                fontWeight: 600, color: C.primary,
                                textDecoration: t.status === "completed" ? "line-through" : "none",
                                opacity: t.status === "completed" ? 0.6 : 1,
                              }}>{t.title}</span>
                              {t.notes && <p style={{ margin: "2px 0 0", fontSize: 11, color: C.textLight }}>{t.notes}</p>}
                            </td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.owner || "—"}</td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.startDate || "—"}</td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.deadline || "—"}</td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>
                              <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${meta.color}15`, color: meta.color }}>
                                {meta.label}
                              </span>
                            </td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.rating || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {canEdit && (
                <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                  <Btn size="sm" variant="ghost" icon={Plus} onClick={() => { setShowTaskFormFor(g.id); setTaskForm(INITIAL_OP_TASK); }}>
                    إضافة مهمة
                  </Btn>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Goal Modal */}
      {showGoalForm && (
        <Modal title="إضافة هدف جديد" onClose={() => { setShowGoalForm(false); setGoalForm(INITIAL_OP_GOAL); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم الهدف" required placeholder="مثال: زيادة عدد المستفيدين 20%" value={goalForm.name} onChange={v => setGoalForm({ ...goalForm, name: v })} />
            <Field label="وصف الهدف" type="textarea" rows={3} placeholder="وصف تفصيلي للهدف..." value={goalForm.description} onChange={v => setGoalForm({ ...goalForm, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المسؤول عن الهدف" placeholder="اسم المسؤول" value={goalForm.owner} onChange={v => setGoalForm({ ...goalForm, owner: v })} />
              <Field label="الأولوية" type="select" options={PRIORITY_OPTIONS} value={goalForm.priority} onChange={v => setGoalForm({ ...goalForm, priority: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={goalForm.startDate} onChange={v => setGoalForm({ ...goalForm, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" value={goalForm.endDate} onChange={v => setGoalForm({ ...goalForm, endDate: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الوزن / النسبة من إجمالي الخطة (%)" type="number" placeholder="مثال: 25" value={goalForm.weight} onChange={v => setGoalForm({ ...goalForm, weight: v })} />
              <Field label="الميزانية المخصصة (ر.س)" type="number" placeholder="0" value={goalForm.budget} onChange={v => setGoalForm({ ...goalForm, budget: v })} />
            </div>
            <Field label="مؤشرات الأداء" type="textarea" rows={3} placeholder="مؤشر 1&#10;مؤشر 2&#10;مؤشر 3" value={goalForm.indicators} onChange={v => setGoalForm({ ...goalForm, indicators: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSaveGoal}>حفظ الهدف</Btn>
              <Btn variant="ghost" onClick={() => { setShowGoalForm(false); setGoalForm(INITIAL_OP_GOAL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Task Modal */}
      {showTaskFormFor && (
        <Modal title="إضافة مهمة" onClose={() => { setShowTaskFormFor(null); setTaskForm(INITIAL_OP_TASK); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان المهمة" required placeholder="عنوان المهمة" value={taskForm.title} onChange={v => setTaskForm({ ...taskForm, title: v })} />
            <Field label="وصف المهمة" type="textarea" rows={2} placeholder="وصف..." value={taskForm.description} onChange={v => setTaskForm({ ...taskForm, description: v })} />
            <Field label="المسؤول" placeholder="اسم المسؤول" value={taskForm.owner} onChange={v => setTaskForm({ ...taskForm, owner: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={taskForm.startDate} onChange={v => setTaskForm({ ...taskForm, startDate: v })} />
              <Field label="الموعد النهائي" type="date" value={taskForm.deadline} onChange={v => setTaskForm({ ...taskForm, deadline: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الحالة" type="select" options={TASK_STATUS_OPTIONS} value={taskForm.status} onChange={v => setTaskForm({ ...taskForm, status: v })} />
              <Field label="التقييم (اختياري)" type="select" options={RATING_OPTIONS.map(r => ({ value: r, label: r || "—" }))} value={taskForm.rating} onChange={v => setTaskForm({ ...taskForm, rating: v })} />
            </div>
            <Field label="الملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={taskForm.notes} onChange={v => setTaskForm({ ...taskForm, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSaveTask}>حفظ المهمة</Btn>
              <Btn variant="ghost" onClick={() => { setShowTaskFormFor(null); setTaskForm(INITIAL_OP_TASK); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ===== downloadReport =====
export function downloadReport(report, opts = {}) {
  const {
    departmentName = "",
    statusLabel = report.status,
    orgName = "جمعية الدعوة والإرشاد وتوعية الجاليات بشمال بريدة",
  } = opts;
  const w = window.open("", "_blank");
  if (!w) { alert("الرجاء السماح بالنوافذ المنبثقة لتحميل التقرير"); return; }
  const esc = (v) => String(v ?? "—").replace(/[&<>]/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;" }[c]));
  const fv = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return s.trim() === "" ? "—" : esc(s);
  };
  const fld = (label, value, long, cls) => {
    const val = fv(value);
    const empty = val === "—" ? " empty" : "";
    return `<div class="fld${long ? " long" : ""}${cls ? " " + cls : ""}"><label>${esc(label)}</label><div class="val${empty}">${val}</div></div>`;
  };
  const badgeStyles = {
    "معتمد": "background:#e6f5ee;color:#2D8B59;",
    "مسودة": "background:#fdf6e3;color:#8a6d1f;",
    "مراجعة": "background:#e6f7f5;color:#00806f;",
  };
  const badgeStyle = badgeStyles[statusLabel] || "background:#f1f5f9;color:#5a7b8f;";
  const statusFld = `<div class="fld status"><label>${esc("الحالة")}</label><div class="val"><span class="badge" style="${badgeStyle}">${fv(statusLabel)}</span></div></div>`;
  const extrasEntries = report.extras && typeof report.extras === "object"
    ? Object.entries(report.extras)
    : [];
  const html = `<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8">
<title>${esc(report.title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Readex+Pro:wght@300;400;500;600&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: 'Readex Pro', sans-serif; color: #003d58; margin: 0; background: #F7F8FA; }
  .page { max-width: 820px; margin: 0 auto; padding: 40px 44px; box-sizing: border-box; background: #fff; box-shadow: 0 1px 3px rgba(0,61,88,.08); }
  .head { border-bottom: 3px solid #dcc160; padding-bottom: 16px; margin-bottom: 30px; }
  .org { font-size: 12px; color: #5a7b8f; letter-spacing: .2px; }
  .dept { font-size: 18px; font-weight: 600; color: #003d58; margin-top: 5px; }
  .doctype { font-size: 11px; color: #00b4a2; font-weight: 600; margin-top: 3px; }
  .fld { margin-bottom: 18px; break-inside: avoid; }
  .fld label { display: block; font-size: 11px; font-weight: 600; color: #5a7b8f; margin-bottom: 6px; letter-spacing: .2px; }
  .fld .val { border: 1px solid #e2e8f0; border-radius: 10px; padding: 11px 14px; font-size: 13.5px; color: #003d58; background: #fff; min-height: 20px; line-height: 1.85; white-space: pre-wrap; }
  .fld.long .val { min-height: 110px; }
  .fld .val.empty { color: #9fb3c0; }
  .fld.title .val { font-size: 16px; font-weight: 600; }
  .fld.status .val { border: none; padding: 0; }
  .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
  .row { display: flex; gap: 16px; }
  .row .fld { flex: 1; margin-bottom: 18px; }
  .foot { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 10.5px; color: #5a7b8f; display: flex; justify-content: space-between; }
  @media print {
    body { background: #fff; }
    .page { max-width: none; margin: 0; padding: 0; box-shadow: none; }
  }
</style></head><body>
<div class="page">
<div class="head">
  <div class="org">${esc(orgName)}</div>
  ${departmentName ? `<div class="dept">${esc(departmentName)}</div>` : ""}
  <div class="doctype">تقرير</div>
</div>
${fld("عنوان التقرير", report.title, false, "title")}
<div class="row">
  ${fld("من تاريخ", report.periodFrom)}
  ${fld("إلى تاريخ", report.periodTo)}
</div>
${fld("نوع التقرير", report.type)}
${fld("الملخص التنفيذي", report.summary, true)}
${fld("التوصيات", report.recommendations, true)}
${fld("المعد", report.preparedBy)}
${statusFld}
${extrasEntries.map(([k, v]) => fld(k, v)).join("")}
<div class="foot"><span>${esc(orgName)}</span><span>طُبع في: ${new Date().toLocaleDateString("ar-SA")}</span></div>
</div>
</body></html>`;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}

// ===== ReportsTab =====
// Per-department report type options + extra fields
const REPORT_TYPES_BASE = ["شهري", "ربع سنوي", "نصف سنوي", "سنوي", "خاص"];
const REPORT_STATUS_OPTIONS = ["مسودة", "مراجعة", "معتمد"];
const REPORT_STATUS_LABEL_TO_KEY = { "مسودة": "draft", "مراجعة": "review", "معتمد": "approved" };
const REPORT_EXTRA_FIELDS = {
  "الجاليات": [
    { key: "beneficiaries", label: "عدد المستفيدين", type: "number" },
    { key: "activePreachers", label: "عدد الدعاة النشطين", type: "number" },
    { key: "programsCount", label: "عدد البرامج", type: "number" },
    { key: "newMuslims", label: "عدد المسلمين الجدد", type: "number" },
    { key: "achievements", label: "أبرز الإنجازات", type: "textarea" },
  ],
  "المساجد": [
    { key: "mosquesServed", label: "عدد المساجد المخدومة", type: "number" },
    { key: "maintenanceDone", label: "عمليات الصيانة المنجزة", type: "number" },
    { key: "totalCost", label: "التكلفة الإجمالية (ر.س)", type: "number" },
    { key: "pendingIssues", label: "المشاكل المعلقة", type: "textarea" },
  ],
  "القسم النسائي": [
    { key: "beneficiaries", label: "عدد المستفيدات", type: "number" },
    { key: "circles", label: "عدد الحلقات", type: "number" },
    { key: "attendanceRate", label: "معدل الحضور (%)", type: "number" },
    { key: "topPrograms", label: "أبرز البرامج", type: "textarea" },
  ],
  "المالية": [
    { key: "totalIncome", label: "إجمالي الإيرادات (ر.س)", type: "number" },
    { key: "totalExpense", label: "إجمالي المصروفات (ر.س)", type: "number" },
    { key: "netResult", label: "الصافي (ر.س)", type: "number" },
    { key: "topExpenses", label: "أكبر بنود الصرف", type: "textarea" },
  ],
  "المتبرعين": [
    { key: "newDonors", label: "عدد المتبرعين الجدد", type: "number" },
    { key: "totalDonations", label: "إجمالي التبرعات (ر.س)", type: "number" },
    { key: "topDonor", label: "أكبر متبرع", type: "text" },
    { key: "retentionRate", label: "نسبة الاحتفاظ (%)", type: "number" },
  ],
  "الموارد البشرية": [
    { key: "employeesCount", label: "عدد الموظفين", type: "number" },
    { key: "newHires", label: "التعيينات الجديدة", type: "number" },
    { key: "resignations", label: "الاستقالات", type: "number" },
    { key: "trainingHours", label: "ساعات التدريب", type: "number" },
  ],
  "التطوع": [
    { key: "volunteersCount", label: "عدد المتطوعين", type: "number" },
    { key: "totalHours", label: "ساعات التطوع", type: "number" },
    { key: "eventsExecuted", label: "الفعاليات المنفذة", type: "number" },
  ],
  "الاتصال المؤسسي": [
    { key: "newsPublished", label: "عدد الأخبار المنشورة", type: "number" },
    { key: "followerGrowth", label: "نمو المتابعين (%)", type: "number" },
    { key: "mediaCoverage", label: "التغطيات الإعلامية", type: "number" },
  ],
  "الحوكمة": [
    { key: "governanceScore", label: "درجة الحوكمة (%)", type: "number" },
    { key: "closedIssues", label: "الملاحظات المغلقة", type: "number" },
    { key: "renewedLicenses", label: "التراخيص المجددة", type: "number" },
  ],
  "المجلس": [
    { key: "meetingsHeld", label: "عدد الاجتماعات", type: "number" },
    { key: "decisionsTaken", label: "القرارات المتخذة", type: "number" },
    { key: "decisionsImplemented", label: "القرارات المنفذة", type: "number" },
  ],
};

export function ReportsTab({ departmentName = "الإدارة", canEdit = true }) {
  const [reports, setReports] = useState([
    { id: 1, title: `التقرير الشهري — ${departmentName} — محرم 1447`, date: "2026-01-30", type: "شهري", status: "approved", periodFrom: "2026-01-01", periodTo: "2026-01-30", preparedBy: "مدير القسم" },
    { id: 2, title: `التقرير الشهري — ${departmentName} — صفر 1447`, date: "2026-02-28", type: "شهري", status: "approved", periodFrom: "2026-02-01", periodTo: "2026-02-28", preparedBy: "مدير القسم" },
    { id: 3, title: `تقرير الربع الأول — ${departmentName}`, date: "2026-03-30", type: "ربع سنوي", status: "draft", periodFrom: "2026-01-01", periodTo: "2026-03-30", preparedBy: "مدير القسم" },
  ]);
  const extraFields = REPORT_EXTRA_FIELDS[departmentName] || [];
  const initialExtras = Object.fromEntries(extraFields.map(f => [f.key, ""]));
  const INITIAL_REPORT_FORM = {
    title: "", periodFrom: "", periodTo: "", type: "شهري",
    summary: "", recommendations: "", preparedBy: "", statusLabel: "مسودة",
    ...initialExtras,
  };
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_REPORT_FORM);
  const [showToast, ToastView] = useToast();

  const statusLabels = { approved: "معتمد", draft: "مسودة", review: "مراجعة" };
  const statusColors = { approved: C.success, draft: C.textLight, review: C.accent };

  function handleSave() {
    if (!form.title || !form.periodFrom || !form.periodTo) {
      showToast("العنوان والفترة الزمنية مطلوبة", "error");
      return;
    }
    const newR = {
      id: Date.now(),
      title: form.title,
      date: new Date().toISOString().slice(0, 10),
      type: form.type,
      status: REPORT_STATUS_LABEL_TO_KEY[form.statusLabel] || "draft",
      periodFrom: form.periodFrom,
      periodTo: form.periodTo,
      summary: form.summary,
      recommendations: form.recommendations,
      preparedBy: form.preparedBy || "—",
      extras: Object.fromEntries(extraFields.map(f => [f.key, form[f.key]])),
    };
    setReports(prev => [newR, ...prev]);
    setForm(INITIAL_REPORT_FORM);
    setShowForm(false);
    showToast("تم إنشاء التقرير");
  }

  return (
    <div>
      {ToastView}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.primary, margin: 0 }}>تقارير {departmentName}</h3>
        {canEdit && <Btn icon={Plus} onClick={() => setShowForm(true)}>إنشاء تقرير جديد</Btn>}
      </div>
      <div style={{ display: "grid", gap: 14 }}>
        {reports.map((r) => (
          <Card key={r.id} onClick={() => setSelected(r)} style={{ cursor: "pointer", borderRight: `4px solid ${statusColors[r.status]}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: C.primary, margin: 0, marginBottom: 6 }}>{r.title}</h4>
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: C.textLight }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={13} /> {r.date}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FileText size={13} /> {r.type}</span>
                  {r.preparedBy && <span>المعد: {r.preparedBy}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{
                  padding: "5px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  background: `${statusColors[r.status]}15`, color: statusColors[r.status],
                }}>
                  {statusLabels[r.status]}
                </span>
                <Btn variant="ghost" size="sm" icon={Download} onClick={() => downloadReport(r, {
                  departmentName,
                  statusLabel: statusLabels[r.status] || r.status,
                })}>تحميل</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 13 }}>
              <div><strong style={{ color: C.textLight }}>النوع:</strong> {selected.type}</div>
              <div><strong style={{ color: C.textLight }}>الفترة:</strong> {selected.periodFrom} → {selected.periodTo}</div>
              <div><strong style={{ color: C.textLight }}>الحالة:</strong> {statusLabels[selected.status]}</div>
            </div>
            {selected.preparedBy && <div style={{ fontSize: 13 }}><strong>المعد:</strong> {selected.preparedBy}</div>}
            {selected.summary && (
              <div>
                <strong style={{ color: C.primary }}>الملخص التنفيذي:</strong>
                <p style={{ margin: "4px 0 0", padding: 12, background: C.bg, borderRadius: 8, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.summary}</p>
              </div>
            )}
            {selected.recommendations && (
              <div>
                <strong style={{ color: C.primary }}>التوصيات:</strong>
                <p style={{ margin: "4px 0 0", padding: 12, background: `${C.teal}06`, borderRadius: 8, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.recommendations}</p>
              </div>
            )}
            {selected.extras && Object.keys(selected.extras).length > 0 && (
              <div>
                <strong style={{ color: C.primary }}>بيانات القسم:</strong>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                  {extraFields.map(f => selected.extras[f.key] ? (
                    <div key={f.key} style={{ padding: 10, background: C.bg, borderRadius: 8, fontSize: 13 }}>
                      <span style={{ color: C.textLight }}>{f.label}:</span> <strong>{selected.extras[f.key]}</strong>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showForm && (
        <Modal title={`إنشاء تقرير — ${departmentName}`} onClose={() => { setShowForm(false); setForm(INITIAL_REPORT_FORM); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان التقرير" required value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="من تاريخ" type="date" required value={form.periodFrom} onChange={v => setForm({ ...form, periodFrom: v })} />
              <Field label="إلى تاريخ" type="date" required value={form.periodTo} onChange={v => setForm({ ...form, periodTo: v })} />
              <Field label="نوع التقرير" type="select" options={REPORT_TYPES_BASE} value={form.type} onChange={v => setForm({ ...form, type: v })} />
            </div>

            {/* Department-specific extra fields */}
            {extraFields.length > 0 && (
              <div style={{ padding: 14, background: `${C.teal}06`, borderRadius: 10, border: `1px solid ${C.teal}20` }}>
                <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal }}>
                  بيانات قسم {departmentName}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: extraFields.some(f => f.type === "textarea") ? "1fr" : "1fr 1fr", gap: 10 }}>
                  {extraFields.map(f => (
                    <Field
                      key={f.key}
                      label={f.label}
                      type={f.type}
                      rows={f.type === "textarea" ? 2 : undefined}
                      value={form[f.key] || ""}
                      onChange={v => setForm({ ...form, [f.key]: v })}
                    />
                  ))}
                </div>
              </div>
            )}

            <Field label="الملخص التنفيذي" type="textarea" rows={4} value={form.summary} onChange={v => setForm({ ...form, summary: v })} />
            <Field label="التوصيات" type="textarea" rows={3} value={form.recommendations} onChange={v => setForm({ ...form, recommendations: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المعد" placeholder="اسم معد التقرير" value={form.preparedBy} onChange={v => setForm({ ...form, preparedBy: v })} />
              <Field label="الحالة" type="select" options={REPORT_STATUS_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSave}>حفظ التقرير</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(INITIAL_REPORT_FORM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== ColorTemplateSelector =====
export function ColorTemplateSelector({ onSelect, selected }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
      {COLOR_TEMPLATES.map((t, i) => (
        <div
          key={i}
          onClick={() => onSelect(t)}
          style={{
            padding: 12,
            borderRadius: 10,
            border: `2px solid ${selected?.name === t.name ? C.teal : C.border}`,
            cursor: "pointer",
            textAlign: "center",
            transition: "all 0.2s",
            background: selected?.name === t.name ? `${C.teal}08` : C.white,
          }}
        >
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: t.primary }} />
            <div style={{ width: 28, height: 28, borderRadius: 6, background: t.secondary, border: `1px solid ${C.border}` }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 500, color: C.text }}>{t.name}</span>
        </div>
      ))}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ تصدير واستيراد CSV
   ─────────────────────────────────────────────
   BOM في المقدمة ضروري حتى يفتح Excel العربية بترميز صحيح.
   columns: [{ key, header }] — نفس شكل أعمدة DataTable.
   ═════════════════════════════════════════════ */

function csvCell(val) {
  if (val === null || val === undefined) return "";
  let v = Array.isArray(val) ? val.join(" | ") : String(val);
  if (/[",\n\r]/.test(v)) v = '"' + v.replace(/"/g, '""') + '"';
  return v;
}

export function buildCSV(data, columns) {
  const header = columns.map(c => csvCell(c.header ?? c.label ?? c.key)).join(",");
  const rows = (data || []).map(row =>
    columns.map(c => csvCell(typeof c.value === "function" ? c.value(row) : row[c.key])).join(",")
  );
  return "\uFEFF" + [header, ...rows].join("\n");
}

function downloadBlob(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportToCSV(data, columns, filename) {
  downloadBlob(buildCSV(data, columns), `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
}

export function downloadCSVTemplate(columns, filename) {
  downloadBlob(buildCSV([], columns), `${filename}_template.csv`);
}

// محلّل CSV يحترم علامات الاقتباس والفواصل والأسطر داخل الخلايا
export function parseCSV(text) {
  const src = text.replace(/^\uFEFF/, "");
  const rows = [];
  let row = [], cell = "", quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }
        else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') { quoted = true; continue; }
    if (ch === ",") { row.push(cell); cell = ""; continue; }
    if (ch === "\r") continue;
    if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; continue; }
    cell += ch;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.filter(r => r.some(c => String(c).trim() !== ""));
}

/* ─── ImportModal — استيراد CSV مع معاينة وتحقق ─── */
export function ImportModal({ title = "استيراد بيانات", columns, onImport, onClose, entityLabel = "سجل" }) {
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [fileName, setFileName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const headers = columns.map(c => c.header ?? c.label ?? c.key);

  function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setRows(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const table = parseCSV(String(reader.result || ""));
        if (table.length < 2) {
          setError("الملف فارغ أو لا يحتوي على صفوف بيانات");
          return;
        }
        const fileHeaders = table[0].map(h => h.trim());
        const missing = headers.filter(h => !fileHeaders.includes(h));
        if (missing.length) {
          setError(`أعمدة ناقصة في الملف: ${missing.join("، ")}`);
          return;
        }
        const idx = Object.fromEntries(columns.map(c => [c.key, fileHeaders.indexOf(c.header ?? c.label ?? c.key)]));
        const parsed = table.slice(1).map(r =>
          Object.fromEntries(columns.map(c => [c.key, (r[idx[c.key]] ?? "").trim()]))
        );
        setRows(parsed);
      } catch (err) {
        setError("تعذّرت قراءة الملف — تأكّد أنه بصيغة CSV");
      }
    };
    reader.onerror = () => setError("تعذّرت قراءة الملف");
    reader.readAsText(file, "utf-8");
  }

  async function handleConfirm() {
    if (!rows || !rows.length) return;
    setBusy(true);
    try {
      await onImport(rows);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose} width={720}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ padding: 12, background: C.bg, borderRadius: 8, fontSize: 12.5, color: C.text, lineHeight: 1.8 }}>
          الأعمدة المطلوبة بالترتيب:
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
            {headers.map(h => (
              <span key={h} style={{ padding: "2px 8px", background: C.white, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 11, fontWeight: 600 }}>{h}</span>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Btn icon={Download} variant="ghost" size="sm" onClick={() => downloadCSVTemplate(columns, entityLabel)}>
            تحميل القالب
          </Btn>
          <label style={{
            display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 14px",
            borderRadius: 8, border: `1px dashed ${C.teal}`, color: C.teal,
            cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "'Readex Pro', sans-serif",
          }}>
            <Upload size={15} />
            {fileName || "اختر ملف CSV"}
            <input type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: "none" }} />
          </label>
        </div>

        {error && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: `${C.danger}10`, color: C.danger, borderRadius: 8, fontSize: 12.5 }}>
            <AlertCircle size={15} /> {error}
          </div>
        )}

        {rows && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 8 }}>
              معاينة — {rows.length} {entityLabel} (أول 5 صفوف)
            </div>
            <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {headers.map(h => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: "right", background: C.bg, color: C.primary, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i}>
                      {columns.map(c => (
                        <td key={c.key} style={{ padding: "7px 10px", borderTop: `1px solid ${C.borderLight}`, color: C.text, whiteSpace: "nowrap" }}>
                          {r[c.key] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <Btn icon={Upload} onClick={handleConfirm} disabled={!rows || !rows.length || busy}>
            {busy ? "جارٍ الاستيراد..." : `استيراد ${rows ? rows.length : 0} ${entityLabel}`}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
        </div>
      </div>
    </Modal>
  );
}

export function canSeeTab(perms, moduleKey, tabKey) {
  if (!perms || !perms.tabs || perms.tabs === "*") return true;
  const allowed = perms.tabs[moduleKey];
  if (!allowed) return true;
  return allowed.includes(tabKey);
}

export function filterByOwner(rows, user, perms) {
  if (!perms || perms.scope !== "own") return rows || [];
  return (rows || []).filter(r =>
    r.created_by === user?.id || r.preacher === user?.name || r.preacherId === user?.id
  );
}

// ===== RequestsTab — Self-Service Requests with approval lifecycle =====
function nextStatusFor(category, currentStatus, decision) {
  if (decision === "reject") return "rejected";
  const route = APPROVAL_ROUTES[category] || [];
  const map = {
    leave:   { new: "pending_manager", pending_manager: "approved_manager", approved_manager: "approved_final" },
    hr:      { new: "pending_manager", pending_manager: "approved_manager", approved_manager: "approved_final" },
    finance: { new: "pending_manager", pending_manager: "approved_manager", approved_manager: "approved_final" },
    admin:   { new: "referred", referred: "completed" },
  };
  const flow = map[category] || {};
  return flow[currentStatus] || (route.length ? "approved_final" : "completed");
}

function categoryLabel(key) {
  return (REQUEST_CATEGORIES.find(c => c.key === key) || {}).label || key;
}

export function RequestsTab({ user, perms, scope = "all" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supaError, setSupaError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [trail, setTrail] = useState([]);
  const [actionNote, setActionNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setSupaError(false);
    try {
      const data = await getRequests("order=created_at.desc");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setSupaError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const list = rows || [];
    if (scope === "own") return list.filter(r => r.created_by === user?.id || r.applicant_id === user?.id);
    if (scope === "finance") return list.filter(r => r.category === "finance");
    if (scope === "manager") return list.filter(r => r.department && user?.department && r.department === user.department);
    return list;
  }, [rows, scope, user]);

  const openRow = async (row) => {
    setSelected(row);
    setTrail([]);
    setActionNote("");
    try {
      const t = await getRequestTrail(row.id);
      setTrail(Array.isArray(t) ? t : []);
    } catch (e) {
      setTrail([]);
    }
  };

  const act = async (decision) => {
    if (!selected) return;
    setBusy(true);
    const route = APPROVAL_ROUTES[selected.category] || [];
    const stageIdx = Math.min(trail.length, Math.max(0, route.length - 1));
    const stage = route[stageIdx] || "—";
    const nextStatus = nextStatusFor(selected.category, selected.status, decision);
    try {
      await advanceRequest(selected.id, {
        stage,
        actorId: user?.id,
        actorName: user?.name,
        decision,
        note: actionNote || null,
        nextStatus,
      });
      setToast({ message: decision === "approve" ? "تم اعتماد الطلب" : "تم رفض الطلب", type: "success" });
      setSelected(null);
      await load();
    } catch (e) {
      setToast({ message: "تعذّر تحديث الطلب — Supabase غير متاح", type: "error" });
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const canAct = perms?.scope !== "own" && scope !== "own";

  const columns = [
    { key: "req_number", label: "رقم الطلب", nowrap: true, render: (v) => <span style={{ fontFamily: "monospace", fontWeight: 700, color: C.teal }}>{v}</span> },
    { key: "category", label: "الفئة", render: (v) => categoryLabel(v) },
    { key: "type", label: "النوع" },
    { key: "applicant_name", label: "مقدّم الطلب" },
    { key: "request_date", label: "التاريخ", nowrap: true },
    { key: "status", label: "الحالة", render: (v) => <StatusBadge status={v} statuses={REQUEST_STATUSES} /> },
    { key: "attachment", label: "المرفق", render: (v) => v ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 12 }}><Paperclip size={12} />{v}</span> : <span style={{ color: C.textLight }}>—</span> },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.primary }}>
            {scope === "own" ? "طلباتي" : "الطلبات"}
          </h3>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textLight }}>
            {visible.length} طلب — {scope === "own" ? "متابعة طلباتك وحالتها" : "إدارة دورة اعتماد الطلبات"}
          </p>
        </div>
        <Btn variant="ghost" icon={RefreshCw} onClick={load} disabled={loading}>تحديث</Btn>
      </div>

      {supaError && (
        <div style={{ padding: "10px 14px", background: `${C.warning}15`, border: `1px solid ${C.warning}40`, borderRadius: 10, color: C.warning, fontSize: 13, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={16} />
          <span>تعذّر الاتصال بـ Supabase — العرض فارغ مؤقتاً. تأكد من إعداد الجداول والـ RLS.</span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.textLight }}>جارٍ التحميل…</div>
      ) : visible.length === 0 ? (
        <EmptyState icon={Inbox} title="لا توجد طلبات" subtitle={scope === "own" ? "لم تُقدّم أي طلب بعد" : "لا توجد طلبات ضمن نطاقك"} />
      ) : (
        <DataTable columns={columns} data={visible} onRowClick={openRow} />
      )}

      {selected && (() => {
        const route = APPROVAL_ROUTES[selected.category] || [];
        const stageIdx = Math.min(trail.length, Math.max(0, route.length - 1));
        const currentStage = route[stageIdx] || "—";
        const isClosed = ["approved_final", "rejected", "completed"].includes(selected.status);
        return (
          <Modal title={`تفاصيل الطلب ${selected.req_number || ""}`} onClose={() => setSelected(null)} width={680}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><p style={{ margin: 0, fontSize: 11, color: C.textLight }}>الفئة</p><p style={{ margin: "2px 0 0", fontWeight: 700, color: C.primary }}>{categoryLabel(selected.category)}</p></div>
                <div><p style={{ margin: 0, fontSize: 11, color: C.textLight }}>النوع</p><p style={{ margin: "2px 0 0", fontWeight: 700, color: C.primary }}>{selected.type}</p></div>
                <div><p style={{ margin: 0, fontSize: 11, color: C.textLight }}>مقدّم الطلب</p><p style={{ margin: "2px 0 0", fontWeight: 700, color: C.primary }}>{selected.applicant_name}</p></div>
                <div><p style={{ margin: 0, fontSize: 11, color: C.textLight }}>الإدارة</p><p style={{ margin: "2px 0 0", fontWeight: 700, color: C.primary }}>{selected.department || "—"}</p></div>
                <div><p style={{ margin: 0, fontSize: 11, color: C.textLight }}>التاريخ</p><p style={{ margin: "2px 0 0", fontWeight: 700, color: C.primary }}>{selected.request_date || "—"}</p></div>
                <div><p style={{ margin: 0, fontSize: 11, color: C.textLight }}>الحالة</p><div style={{ marginTop: 4 }}><StatusBadge status={selected.status} statuses={REQUEST_STATUSES} /></div></div>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>السبب</p>
                <p style={{ margin: "4px 0 0", color: C.text, lineHeight: 1.7 }}>{selected.reason}</p>
              </div>
              {selected.notes && (
                <div>
                  <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>ملاحظات</p>
                  <p style={{ margin: "4px 0 0", color: C.text, lineHeight: 1.7 }}>{selected.notes}</p>
                </div>
              )}

              <div>
                <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, color: C.primary }}>مسار الاعتماد</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {route.map((s, i) => {
                    const passed = i < trail.length;
                    const active = i === stageIdx && !isClosed;
                    return (
                      <span key={s} style={{
                        padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: passed ? `${C.success}18` : active ? `${C.warning}18` : `${C.textLight}10`,
                        color: passed ? C.success : active ? C.warning : C.textLight,
                        border: active ? `1px dashed ${C.warning}` : "1px solid transparent",
                      }}>{s}</span>
                    );
                  })}
                </div>
              </div>

              {trail.length > 0 && (
                <div>
                  <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, color: C.primary }}>السجل</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {trail.map(t => (
                      <div key={t.id} style={{ padding: "8px 12px", background: "#fafafa", borderRadius: 8, fontSize: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: 700, color: C.primary }}>{t.stage}</span>
                          <span style={{ color: t.decision === "approve" ? C.success : C.danger, fontWeight: 600 }}>{t.decision === "approve" ? "اعتماد" : "رفض"}</span>
                        </div>
                        <div style={{ color: C.textLight, marginTop: 2 }}>{t.actor_name} — {t.created_at ? new Date(t.created_at).toLocaleString("ar") : ""}</div>
                        {t.note && <div style={{ color: C.text, marginTop: 4 }}>{t.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canAct && !isClosed && (
                <>
                  <Field label={`ملاحظة (${currentStage})`} type="textarea" rows={2} value={actionNote} onChange={setActionNote} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn icon={ThumbsUp} onClick={() => act("approve")} disabled={busy}>اعتماد</Btn>
                    <Btn icon={ThumbsDown} variant="ghost" onClick={() => act("reject")} disabled={busy}>رفض</Btn>
                  </div>
                </>
              )}
            </div>
          </Modal>
        );
      })()}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ===== formatFileSize helper (بايت الى نص عربي مقروء) =====
function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} بايت`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} كيلوبايت`;
  return `${(n / (1024 * 1024)).toFixed(1)} ميجابايت`;
}

// ===== FileUpload =====
export function FileUpload({ module, recordId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [showToast, ToastView] = useToast();
  const inputRef = React.useRef(null);

  const maxMB = Math.round(MAX_ATTACHMENT_SIZE / (1024 * 1024));
  const helpText = `الأنواع المسموحة: ${ALLOWED_ATTACHMENT_EXTS.join("، ").toUpperCase()} — الحجم الأقصى ${maxMB} ميجابايت`;

  async function handleSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const validationError = validateAttachmentFile(file);
    if (validationError) {
      showToast(validationError, "error");
      setFileName("");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const path = buildAttachmentPath(module, recordId, file);
      await uploadFile(ATTACHMENTS_BUCKET, path, file);
      await createAttachment({
        module,
        record_id: recordId,
        bucket: ATTACHMENTS_BUCKET,
        path,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
      });
      showToast("تم رفع المرفق بنجاح", "success");
      setFileName("");
      if (inputRef.current) inputRef.current.value = "";
      onUploaded && onUploaded();
    } catch (err) {
      showToast(err?.message || "تعذّر رفع الملف.", "error");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ direction: "rtl", display: "flex", flexDirection: "column", gap: 6 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_ATTACHMENT_EXTS.map((x) => "." + x).join(",")}
        onChange={handleSelect}
        disabled={uploading}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Btn
          variant="teal"
          size="sm"
          icon={uploading ? undefined : Upload}
          disabled={uploading}
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          {uploading ? (
            <>
              <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
              جاري الرفع…
            </>
          ) : (
            "رفع مرفق"
          )}
        </Btn>
        {fileName && (
          <span style={{ fontSize: 13, color: C.textLight, display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <Paperclip size={14} color={C.teal} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{fileName}</span>
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>{helpText}</p>
      {ToastView}
    </div>
  );
}

// ===== AttachmentList =====
export function AttachmentList({ module, recordId, canDelete, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showToast, ToastView] = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getAttachments(module, recordId);
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      showToast(err?.message || "تعذّر جلب المرفقات.", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [module, recordId, showToast]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  async function handleDownload(att) {
    try {
      const url = await getSignedUrl(att.bucket || ATTACHMENTS_BUCKET, att.path);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      showToast(err?.message || "تعذّر تحميل الملف.", "error");
    }
  }

  async function handleDelete(att) {
    if (!window.confirm(`هل تريد حذف المرفق "${att.file_name || ""}"؟`)) return;
    setDeletingId(att.id);
    try {
      await deleteAttachment(att.id);
      await deleteFile(att.bucket || ATTACHMENTS_BUCKET, att.path);
      showToast("تم حذف المرفق", "success");
      await load();
    } catch (err) {
      showToast(err?.message || "تعذّر حذف المرفق.", "error");
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div style={{ direction: "rtl", display: "flex", alignItems: "center", gap: 8, padding: "16px 4px", color: C.textLight, fontSize: 13 }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
        جاري تحميل المرفقات…
        {ToastView}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ direction: "rtl", display: "flex", alignItems: "center", gap: 8, padding: "16px 4px", color: C.textLight, fontSize: 13 }}>
        <Paperclip size={16} />
        لا توجد مرفقات
        {ToastView}
      </div>
    );
  }

  return (
    <div style={{ direction: "rtl", display: "flex", flexDirection: "column", gap: 8 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {items.map((att) => {
        const size = formatFileSize(att.file_size);
        const date = att.created_at ? new Date(att.created_at).toLocaleDateString("ar") : "";
        const isDeleting = deletingId === att.id;
        return (
          <Card key={att.id} style={{ padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: `${C.teal}15`, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <FileText size={20} color={C.teal} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {att.file_name || "مرفق"}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textLight }}>
                  {[size, date].filter(Boolean).join(" — ")}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <Btn variant="ghost" size="sm" icon={Download} onClick={() => handleDownload(att)}>
                  تحميل
                </Btn>
                {canDelete === true && (
                  <Btn variant="danger" size="sm" icon={isDeleting ? undefined : Trash2} disabled={isDeleting} onClick={() => handleDelete(att)}>
                    {isDeleting ? (
                      <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
                    ) : (
                      "حذف"
                    )}
                  </Btn>
                )}
              </div>
            </div>
          </Card>
        );
      })}
      {ToastView}
    </div>
  );
}
